// What the welcome page's views share (WelcomeFrame draws each one): a thing
// clicked to narrow the page's search to it (Drill), what a view says while
// its sessions are read or when there are none (BodyState), the line saying
// review is still being read (ReviewReading), the views named side by side
// under the first screen (ViewSwitch), and review's colours.
import { holdsClause } from "../../lib/workspaceSearch.js";
import { SEVERITY_COLOR } from "../../lib/traceReview.js";
import { Bar, HOT_FILL, LIT_FILL } from "../FlowBoard.jsx";

export const MET = SEVERITY_COLOR.danger;
export const FLAGGED = SEVERITY_COLOR.warn;
export const NUM = new Intl.NumberFormat();
// A share as the board says it: "<1%" for a sliver, none of nothing.
export const pctOf = (n, of) => (of > 0 ? `${n > 0 && n / of < 0.01 ? "<1" : Math.round((n / of) * 100)}%` : "");

// Keyboard focus drawn as pointing is: the fill a shade up, framed a shade
// up, so no browser ring.
export const DRILL_FOCUS = "outline-none focus-visible:bg-[var(--row-hover)] focus-visible:[outline:1px_solid_color-mix(in_srgb,var(--muted)_55%,transparent)] focus-visible:[outline-offset:-1px]";

// A thing a click narrows the search to (`clause`), or widens it back out
// of when the search holds it already (aria-pressed, a shade up), with the
// sections' rows' hover: a fade up of the ground. Without a clause (the
// folded rest), it says what it is and does nothing.
export function Drill({ clause, query, onPick, className = "", style = null, children, ...rest }) {
  if (!clause) return <span className={className} style={style || undefined} {...rest}>{children}</span>;
  const held = holdsClause(query, clause);
  return (
    <button
      type="button"
      onClick={() => onPick(clause)}
      aria-pressed={held}
      className={`text-left transition-colors duration-150 hover:bg-[var(--row-hover)] ${DRILL_FOCUS} ${className}`}
      style={{ "--row-hover": LIT_FILL, ...(held ? { background: HOT_FILL } : null), ...style }}
      data-welcome-drill={clause}
      {...rest}
    >
      {children}
    </button>
  );
}

// What a view shows in place of itself: placeholder bars while its sessions
// are first read, a line when they could not be read or there are none;
// null once there are sessions to draw.
export function BodyState({ facts, rows = 6 }) {
  if (facts.sessions.length) return null;
  if (facts.status === "loading") {
    return (
      <div className="grid content-start gap-[10px] pt-[4px]" aria-busy="true" data-welcome-reading="">
        {Array.from({ length: rows }, (_, i) => <Bar key={i} w={`${30 + ((i * 23) % 40)}%`} dim={i % 2 ? 0.4 : 0.7} />)}
      </div>
    );
  }
  return (
    <p className="m-0 text-[12px] text-[color:var(--muted)]" data-welcome-empty="">
      {facts.status === "error" ? "The traces could not be read." : "No trace matches this search."}
    </p>
  );
}

// Where review left the sessions arrives after them (`facts.reviewed`
// false until then): said where a view would show it.
export function ReviewReading({ facts }) {
  if (facts.reviewed !== false) return null;
  return (
    <span className="inline-flex items-center gap-[6px] text-[11px] text-[color:var(--muted)]" aria-live="polite" data-welcome-review-reading="">
      {facts.reviewFailed ? "The review could not be read" : (
        <>
          <span className="h-[6px] w-[6px] animate-pulse rounded-full bg-[color:var(--green)]" aria-hidden="true" />
          Reading the review
        </>
      )}
    </span>
  );
}

// The views named side by side, as a caption names its views: the one shown
// in ink, the rest faint until pointed at.
export function ViewSwitch({ views, view, onView }) {
  return (
    <span className="flex shrink-0 items-baseline gap-[10px] text-[11px] leading-[16px]" role="group" aria-label="Views of the overview" data-welcome-views="">
      {views.map((v) => {
        const on = v.id === view;
        return (
          <button
            key={v.id}
            type="button"
            aria-pressed={on}
            onClick={on ? undefined : () => onView(v.id)}
            className={`whitespace-nowrap transition-opacity ${on ? "cursor-default text-[color:var(--ink)]" : "text-[color:var(--muted)] opacity-[0.6] hover:opacity-100"}`}
            data-welcome-view-option={v.id}
          >
            {v.label}
          </button>
        );
      })}
    </span>
  );
}
